import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Action } from 'history'
import history from 'history/browser'
import { ROUTES } from '@constants/index'
import {
  drawerHeaderVisibleSelector,
  appVisibleSelector,
  ProgressStep,
  progressStepSelector,
  resetWorkflow,
  showApp,
  appClosingSelector,
  appVisibleAllowedSelector,
  setAppVisibleAllowed,
  appBeforeCloseCallbackSelector,
  hideNavigateBackSelector,
  navigateBackCallbackSelector,
  drawerHideCloseSelector,
} from '@redux/slices/workflowSlice'
import { sessionExpiredSelector } from '@redux/slices/connectorSlice'
import { resetForms } from '@redux/slices/formsSlice'
import { resetSubmitStatus } from '@redux/slices/claimSubmitSlice'
import { resetClaimStatus } from '@redux/slices/claimStatusSlice'
import { resetInsuranceMemberState } from '@redux/slices/insuranceMemberSlice'
import { resetPrescriptionFileInitialState } from '@redux/slices/prescriptionFileSlice'
import { Logger } from '@utils/logger'
import {
  clearAllowDirectNavigation,
  clearLocalBasePath,
  isAllowDirectNavigation,
  isLocationAllowedToProceed,
  setLoginRedirectParam,
} from '@utils/navigationUtil'
import { scrollToDrawerTop } from '@utils/scrollToDrawerTop'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInvokeAction } from '@hooks/useInvokeAction'
import { useWebchat } from '@hooks/useWebchat'
import { useAnalytics } from '@hooks/useAnalytics'
import { useSessionInfo } from '@hooks/useSessionInfo'

type UseAppDrawerResult = {
  isVisible: boolean
  showHeader: boolean
  activeStep: ProgressStep
  onClose: () => void
  onNavigateBack?: () => void
  hideNavigateBack?: boolean
  hideCloseButton?: boolean
}

export const useAppDrawer = (): UseAppDrawerResult => {
  const dispatch = useDispatch()
  const { pushOpenEvent } = useAnalytics()
  const { brand, basePath, basePathSearch, signInPath } = useEnvironment()
  const { isRestricted, isRestrictedCheckPerformed, isAnonymousUser, isGuestUser } = useSessionInfo()
  const location = useLocation()
  const activeStep = useSelector(progressStepSelector)
  const isVisible = useSelector(appVisibleSelector)
  const appClosing = useSelector(appClosingSelector)
  const onBeforeClose = useSelector(appBeforeCloseCallbackSelector)
  const appVisibleAllowed = useSelector(appVisibleAllowedSelector)
  const showHeader = useSelector(drawerHeaderVisibleSelector)
  const hideCloseButton = useSelector(drawerHideCloseSelector)
  const sessionExpired = useSelector(sessionExpiredSelector)
  const hideNavigateBack = useSelector(hideNavigateBackSelector)
  const onNavigateBack = useSelector(navigateBackCallbackSelector)
  const notifyFormAction = useInvokeAction()
  const { invokeWebchat } = useWebchat()

  const basePathWithSearch = `${basePath}${basePathSearch}`

  useEffect(() => {
    const openDrawer = () => {
      dispatch(showApp())
      pushOpenEvent()
      invokeWebchat('hide')
    }

    const handleLoginRedirect = () => {
      if (!isRestricted) {
        openDrawer()
      } else {
        // restore url to basePath if restricted
        if (location.pathname.includes(ROUTES.BASE)) {
          history.replace(basePathWithSearch)
        }
      }
    }

    const handleNonAllowedMatch = () => {
      if (appVisibleAllowed) return

      if (isAllowDirectNavigation()) {
        clearAllowDirectNavigation()
        dispatch(setAppVisibleAllowed())
        return
      }

      const isMatch = window.location.pathname.includes(ROUTES.BASE)

      // reloaded when drawer was open
      if (isMatch && isLocationAllowedToProceed()) {
        dispatch(setAppVisibleAllowed())
        return
      }

      // reject direct url access or last path since reload was different
      if (isMatch) {
        history.replace(basePathWithSearch)
      }

      clearLocalBasePath()
    }

    const isMatch = location.pathname.includes(ROUTES.BASE)
    const isRestrickedCheckReady = isRestrictedCheckPerformed || isGuestUser || isAnonymousUser

    if (isMatch && !isVisible && !appClosing && isRestrickedCheckReady) {
      // allowed navigation or page reload while drawer open
      if (appVisibleAllowed || isVisible) {
        const loginRedirect = window.location.pathname.includes(ROUTES.LOGIN_REDIRECT)
        if (loginRedirect) {
          handleLoginRedirect()
        } else {
          openDrawer()
        }
      } else {
        handleNonAllowedMatch()
      }
    }
  }, [
    dispatch,
    pushOpenEvent,
    invokeWebchat,
    basePathWithSearch,
    brand,
    location,
    appVisibleAllowed,
    isVisible,
    appClosing,
    isRestricted,
    isRestrictedCheckPerformed,
    isGuestUser,
    isAnonymousUser,
  ])

  const resetState = useCallback(() => {
    invokeWebchat('reset')
    dispatch(resetForms())
    dispatch(resetWorkflow())
    dispatch(resetClaimStatus())
    dispatch(resetSubmitStatus())
    dispatch(resetInsuranceMemberState())
    dispatch(resetPrescriptionFileInitialState())
  }, [invokeWebchat, dispatch])

  /**
   * On back navigation, clean up state if drawer is not closed
   */
  useEffect(() => {
    if (!isVisible) return

    const unlisten = history.listen(({ location, action }) => {
      // cleanup listener first as using hook cleanup had a side
      // effect of removing the listener before this code can execute
      unlisten()

      if (action === Action.Pop && isVisible) {
        // back navigation when drawer still open, need to reset state
        const pathMatch = location.pathname.includes(`${ROUTES.BASE}`)
        if (!pathMatch) {
          resetState()
        }
      }
    })
  }, [resetState, isVisible])

  const cleanupOnClose = useCallback(() => {
    // replace our history with base path (e.g. cart or checkout)
    // so that back button will work correctly
    history.replace(basePathWithSearch)
    resetState()
    clearLocalBasePath()
  }, [resetState, basePathWithSearch])

  useEffect(() => {
    if (isVisible && appClosing) {
      cleanupOnClose()
    }
  }, [cleanupOnClose, isVisible, appClosing])

  /**
   * Step navigation
   */
  useEffect(() => {
    if (isVisible && !appClosing) {
      scrollToDrawerTop()
      notifyFormAction()
      invokeWebchat('hide')
    }
  }, [notifyFormAction, invokeWebchat, activeStep, isVisible, appClosing])

  useEffect(() => {
    if (sessionExpired) {
      // to allow commerce app to redirect back on next sign in
      if (isVisible) {
        setLoginRedirectParam({ basePath, signInPath })
      }
    }
  }, [basePath, signInPath, isVisible, sessionExpired])

  /**
   * Handle app drawer close button - invokes onBeforeClose to perform optional, view specific
   * cleanup.
   */
  const onClose = () => {
    if (!onBeforeClose || typeof onBeforeClose !== 'function') {
      cleanupOnClose()
      return
    }

    onBeforeClose()
      .catch(error => Logger.warn('Error on app drawer before close', error))
      .finally(cleanupOnClose)
  }

  return {
    isVisible,
    showHeader,
    activeStep,
    onClose,
    onNavigateBack,
    hideNavigateBack,
    hideCloseButton,
  }
}
